import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PauseCircle, ChatCircleText, FloppyDisk, Warning, Clock, PlayCircle, CheckCircle } from "@phosphor-icons/react";

const _UNUSED_REASONS = []; // removed dropdown — reason is now a free-text input

function fmtAt(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, { hour12: false });
}

function fmtElapsed(ms) {
    if (ms < 0) ms = 0;
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function ActivePausePanel({ vessel, pauseState, onUpdated }) {
    const { user } = useAuth();
    const canEdit = user && (user.role === "supervisor" || user.role === "manager" || user.role === "admin");
    const [reason, setReason] = useState(pauseState.reason || "");
    const [description, setDescription] = useState(pauseState.description || "");
    const [etaH, setEtaH] = useState(pauseState.eta_minutes != null ? String(Math.floor(pauseState.eta_minutes / 60)) : "");
    const [etaM, setEtaM] = useState(pauseState.eta_minutes != null ? String(pauseState.eta_minutes % 60) : "");
    const [saving, setSaving] = useState(false);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(t);
    }, []);

    // Re-sync when parent provides new state (e.g. after another user updates)
    useEffect(() => {
        setReason(pauseState.reason || "");
        setDescription(pauseState.description || "");
        setEtaH(pauseState.eta_minutes != null ? String(Math.floor(pauseState.eta_minutes / 60)) : "");
        setEtaM(pauseState.eta_minutes != null ? String(pauseState.eta_minutes % 60) : "");
    }, [pauseState.reason, pauseState.description, pauseState.eta_minutes]);

    const startedAtMs = useMemo(
        () => (pauseState.started_at ? new Date(pauseState.started_at).getTime() : now),
        [pauseState.started_at, now]
    );
    const elapsedMs = now - startedAtMs;
    const etaMs = pauseState.eta_minutes != null ? pauseState.eta_minutes * 60_000 : null;
    const remainingEtaMs = etaMs != null ? etaMs - elapsedMs : null;
    const overrunningEta = remainingEtaMs != null && remainingEtaMs < 0;

    const save = async () => {
        if (!reason.trim()) {
            toast.error("Reason is required");
            return;
        }
        const totalMinutes =
            etaH === "" && etaM === ""
                ? null
                : (parseInt(etaH || "0", 10) || 0) * 60 + (parseInt(etaM || "0", 10) || 0);
        setSaving(true);
        try {
            const { data } = await api.patch(`/vessels/${vessel.id}/pause`, {
                reason: reason.trim(),
                description,
                eta_minutes: totalMinutes,
            });
            toast.success("Breakdown updated");
            onUpdated?.(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="g-card mb-6 overflow-hidden border-2 border-[var(--grindrod-red)] shadow-sm" data-testid="active-pause-panel">
            <div className="bg-[var(--grindrod-red)] text-white">
                {/* Top row: icon + title block */}
                <div className="px-4 sm:px-5 pt-4 pb-2 flex items-start gap-3">
                    <div className="bg-white/15 rounded-full w-10 h-10 flex items-center justify-center shrink-0">
                        <PauseCircle size={22} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.18em] text-white/80 uppercase">
                            Active Breakdown · Loading Paused
                        </div>
                        <div
                            className="font-display font-bold text-base sm:text-lg leading-snug mt-0.5 break-words"
                            data-testid="pause-current-reason"
                            title={pauseState.reason}
                        >
                            {pauseState.reason || "Paused"}
                        </div>
                    </div>
                </div>
                {/* Stats row: paused-for + ETA — even grid, never overlap */}
                <div className={`grid ${etaMs != null ? "grid-cols-2" : "grid-cols-1"} gap-px bg-white/15 mt-1`}>
                    <div className="bg-[var(--grindrod-red)] px-4 sm:px-5 py-3">
                        <div className="text-[10px] font-bold tracking-[0.18em] text-white/80 uppercase">
                            Paused for
                        </div>
                        <div className="font-display font-black tabular text-2xl sm:text-3xl leading-none mt-1" data-testid="pause-elapsed">
                            {fmtElapsed(elapsedMs)}
                        </div>
                    </div>
                    {etaMs != null && (
                        <div className={`px-4 sm:px-5 py-3 ${overrunningEta ? "bg-[#9c0009]" : "bg-[#c10010]"}`}>
                            <div className="text-[10px] font-bold tracking-[0.18em] text-white/85 uppercase flex items-center gap-1">
                                {overrunningEta && <Warning size={10} weight="fill" />}
                                {overrunningEta ? "ETA overrun by" : "ETA repair in"}
                            </div>
                            <div className="font-display font-black tabular text-2xl sm:text-3xl leading-none mt-1" data-testid="pause-eta-remaining">
                                {fmtElapsed(Math.abs(remainingEtaMs))}
                            </div>
                        </div>
                    )}
                </div>
                {/* Millwright acknowledgement strip — surfaces who is attending */}
                {pauseState.accountability === "grindrod" && (
                    <div className="px-4 sm:px-5 py-2 bg-black/20 text-[11px] sm:text-xs flex items-center gap-2 flex-wrap" data-testid="millwright-ack-strip">
                        {pauseState.acknowledged_by_millwright_name ? (
                            <>
                                <span className="inline-flex items-center gap-1 bg-[#16A34A] text-white px-2 py-0.5 rounded-sm uppercase tracking-wide text-[10px] font-bold">
                                    <Clock size={10} weight="fill" /> Maintenance attending
                                </span>
                                <span className="text-white">
                                    <strong>{pauseState.acknowledged_by_millwright_name}</strong> accepted at <span className="tabular font-semibold">{fmtAt(pauseState.acknowledged_by_millwright_at)}</span>
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="inline-flex items-center gap-1 bg-amber-400 text-amber-900 px-2 py-0.5 rounded-sm uppercase tracking-wide text-[10px] font-bold animate-pulse">
                                    <Warning size={10} weight="fill" /> Awaiting millwright accept
                                </span>
                                <span className="text-white/85">
                                    Notification raised — on-shift millwright will accept shortly.
                                </span>
                            </>
                        )}
                    </div>
                )}

                {/* Ready-to-start banner — millwright signalled the vessel is ready, supervisor needs to resume */}
                {pauseState.ready_to_start && (
                    <div className="px-4 sm:px-5 py-3 bg-[var(--grindrod-green)] text-white flex items-center gap-3 flex-wrap" data-testid="ready-to-resume-strip">
                        <CheckCircle size={22} weight="fill" className="shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-black uppercase tracking-wide text-xs sm:text-sm">
                                Millwright says vessel is READY TO START
                            </div>
                            <div className="text-[11px] sm:text-xs text-white/90">
                                <strong>{pauseState.ready_by_millwright_name}</strong> signalled ready at <span className="tabular font-semibold">{fmtAt(pauseState.ready_at)}</span>
                                {pauseState.ready_note ? ` · "${pauseState.ready_note}"` : ""}
                                {user?.role === "supervisor" ? " — tap Resume on the dashboard to confirm and resume loading." : " — awaiting supervisor to resume."}
                            </div>
                        </div>
                        {user?.role === "supervisor" && (
                            <button
                                onClick={async () => {
                                    if (!window.confirm("Resume vessel loading now? This will end the breakdown pause.")) return;
                                    try {
                                        await api.post(`/vessels/${vessel.id}/resume`);
                                        toast.success("Vessel resumed");
                                        onUpdated?.();
                                    } catch (err) {
                                        toast.error(formatApiError(err));
                                    }
                                }}
                                className="bg-white text-[var(--grindrod-green)] hover:bg-emerald-50 font-bold uppercase tracking-wide text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-sm inline-flex items-center gap-2"
                                data-testid="resume-from-ready-btn"
                            >
                                <PlayCircle size={16} weight="fill" /> Resume loading
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="p-5 grid md:grid-cols-2 gap-5">
                <div>
                    <label className="uppercase-label block mb-1.5">
                        Reason <span className="text-[var(--grindrod-red)]">*</span>
                    </label>
                    <input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="g-input"
                        placeholder="Enter the reason for the pause"
                        disabled={!canEdit}
                        data-testid="pause-edit-reason"
                    />
                </div>
                <div>
                    <label className="uppercase-label block mb-1.5 flex items-center gap-1">
                        <Clock size={12} /> Estimated repair time <span className="text-slate-400 normal-case lowercase tracking-normal">(adjustable)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <input
                                type="number"
                                min="0"
                                value={etaH}
                                onChange={(e) => setEtaH(e.target.value)}
                                className="g-input tabular"
                                placeholder="0"
                                disabled={!canEdit}
                                data-testid="pause-edit-eta-hours"
                            />
                            <div className="text-[10px] text-slate-500 mt-0.5">hours</div>
                        </div>
                        <div>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={etaM}
                                onChange={(e) => setEtaM(e.target.value)}
                                className="g-input tabular"
                                placeholder="30"
                                disabled={!canEdit}
                                data-testid="pause-edit-eta-minutes"
                            />
                            <div className="text-[10px] text-slate-500 mt-0.5">minutes</div>
                        </div>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">
                        Started at <span className="tabular font-semibold">{fmtAt(pauseState.started_at)}</span>
                        {pauseState.started_by_name && <> by {pauseState.started_by_name}</>}.
                        {pauseState.last_updated_at && (
                            <> Last update: <span className="tabular">{fmtAt(pauseState.last_updated_at)}</span>{pauseState.last_updated_by_name && <> by {pauseState.last_updated_by_name}</>}.</>
                        )}
                    </div>
                </div>
                <div className="md:col-span-2">
                    <label className="uppercase-label block mb-1.5">Action taken / repair update</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={2}
                        className="g-input"
                        placeholder="e.g. Mechanic on site, replacing gearbox bearing"
                        disabled={!canEdit}
                        data-testid="pause-edit-description"
                    />
                </div>
                <div className="md:col-span-2 flex items-center justify-between gap-3 pt-3 border-t border-slate-100 flex-wrap">
                    <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
                        <ChatCircleText size={14} weight="duotone" className="text-[var(--grindrod-navy)]" />
                        Need a repair-time decision? Use the <strong className="text-[var(--grindrod-navy)]">Feedback &amp; Messages</strong> thread below to chat with management.
                    </div>
                    {canEdit && (
                        <button
                            onClick={save}
                            disabled={saving}
                            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                            data-testid="pause-save-btn"
                        >
                            <FloppyDisk size={14} weight="fill" />
                            {saving ? "Saving…" : "Post update"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
